/**
 * What a file actually IS, read from its bytes rather than its name.
 *
 * `File.type` is not a measurement. The browser derives it from the file
 * extension, so renaming a JPEG to `.mp4` makes `file.type` report
 * `video/mp4`, an `accept="video/*"` picker offer it, and any check written
 * against `file.type` wave it through. On the tool pages that mattered
 * because the file then went all the way: uploaded, submitted, charged for,
 * accepted by fal at submit and refused at render — a silent loss of credits
 * for something no check had actually looked at.
 *
 * So this reads the container signature. It is deliberately DECISIVE-OR-SILENT:
 * a recognised signature is an answer the caller may act on, and an
 * unrecognised one returns `null` rather than a guess, because the long tail of
 * legitimate containers is longer than any table here and refusing an unknown
 * one would break real uploads. The server-side probe is the gate that closes;
 * this is the one that gives an instant, honest answer before the upload.
 */

export type MediaFileKind = 'image' | 'video' | 'audio'

/** Enough for every signature below; the longest lives at offset 8. */
const SIGNATURE_BYTES = 16

const ascii = (bytes: Uint8Array, start: number, end: number): string =>
  String.fromCharCode(...bytes.slice(start, end))

const startsWith = (bytes: Uint8Array, ...expected: number[]): boolean =>
  expected.every((byte, i) => bytes[i] === byte)

/**
 * ISO base media (`....ftyp<brand>`) covers MP4, MOV, M4A, AVIF and HEIC alike
 * — the brand at offset 8 is the only thing separating a video from a still.
 */
function isoBaseMediaKind(brand: string): MediaFileKind | null {
  if (/^(avif|avis|heic|heix|hevc|hevx|mif1|msf1)/.test(brand)) return 'image'
  if (/^(M4A|M4B|M4P|F4A|F4B)/.test(brand)) return 'audio'
  if (/^(isom|iso[2-9]|mp4[12v]|avc1|dash|qt {2}|M4V|3gp|3g2|mmp4)/.test(brand))
    return 'video'
  return null
}

/** The container kind these bytes declare, or null when unrecognised. */
export function mediaKindFromSignature(
  bytes: Uint8Array,
): MediaFileKind | null {
  if (bytes.length < 4) return null

  // RIFF wraps three different things; the form type at offset 8 decides.
  if (ascii(bytes, 0, 4) === 'RIFF') {
    const form = ascii(bytes, 8, 12)
    if (form === 'WEBP') return 'image'
    if (form === 'AVI ') return 'video'
    if (form === 'WAVE') return 'audio'
    return null
  }

  if (ascii(bytes, 4, 8) === 'ftyp')
    return isoBaseMediaKind(ascii(bytes, 8, 12))

  if (startsWith(bytes, 0xff, 0xd8, 0xff)) return 'image'
  if (startsWith(bytes, 0x89, 0x50, 0x4e, 0x47)) return 'image'
  if (ascii(bytes, 0, 3) === 'GIF') return 'image'
  if (ascii(bytes, 0, 2) === 'BM') return 'image'

  // EBML (WebM / Matroska) is deliberately absent, for the same reason as
  // MPEG-TS below: the signature is shared by video and audio-only files, and
  // a decisive WRONG answer here refuses a legitimate upload. Only the doctype
  // inside would settle it, and reading that is the server probe's job.
  // MPEG program stream. Transport stream is deliberately absent: its only
  // marker is a 0x47 byte every 188 bytes, and matching on the first one alone
  // would classify every file that happens to start with "G" as video.
  if (startsWith(bytes, 0x00, 0x00, 0x01, 0xba)) return 'video'

  // Ogg, same reason: .ogv is video and .ogg is audio, and the container
  // header does not say which.
  if (ascii(bytes, 0, 4) === 'fLaC') return 'audio'
  if (ascii(bytes, 0, 3) === 'ID3') return 'audio'
  // MPEG audio frame sync.
  if (bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) return 'audio'

  return null
}

/**
 * Read a picked file's real kind. `null` means "no recognised signature",
 * which callers must treat as unknown rather than as a rejection.
 */
export async function readMediaFileKind(
  file: File,
): Promise<MediaFileKind | null> {
  try {
    const head = await file.slice(0, SIGNATURE_BYTES).arrayBuffer()
    return mediaKindFromSignature(new Uint8Array(head))
  } catch {
    // A file the browser will not let us read is not a file we can judge.
    return null
  }
}
